import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  ListItemIcon,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import MessageIcon from '@mui/icons-material/Message';
import WorkIcon from '@mui/icons-material/Work';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { formatDistanceToNow } from 'date-fns';
import {
  NotificationContainer,
  NotificationHeader,
  NotificationList,
  LoadMoreButton,
  StyledBox,
  StyledListItem,
  StyledListItemText,
  EmptyNotificationsBox,
  LoadingBox,
  StyledNotificationIcon,
  StyledUnreadIndicator,
  MarkAllAsReadButton,
  ShiftSwapListItem,
  ShiftSwapIcon,
  ShiftSwapActionBox,
  ShiftSwapActionText,
  ShiftSwapArrowIcon,
  ShiftSwapListItemIcon,
  ShiftSwapTitle,
  ShiftSwapMessage,
  ShiftSwapTime,
  ErrorDisplayBox,
} from './NotificationDropdown.styles';
import {
  fetchNotificationsThunk,
  markNotificationAsReadThunk,
  selectNotifications,
  selectNotificationsStatus,
  selectHasMoreNotifications,
  selectLastDoc,
  clearNotifications,
  markAllNotificationsAsReadThunk,
} from '../../redux/notifications/notificationsSlice';
import ShiftSwapModal from '../ShiftInformation/components/ShiftSwapModal/ShiftSwapModal';

const NOTIFICATION_ICONS = {
  SHIFT_UPDATE: WorkIcon,
  SCHEDULE_UPDATE: EventIcon,
  MESSAGE: MessageIcon,
  GENERAL: NotificationsIcon,
  SWAP_REQUESTED: SwapHorizIcon,
  SWAP_ACCEPTED: SwapHorizIcon,
  SWAP_DECLINED: SwapHorizIcon,
};

// Filter values
const FILTER_VALUES = {
  ALL: 'all',
  READ: 'read',
  UNREAD: 'unread',
};

const getNotificationIcon = (type) => {
  const IconComponent = NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.GENERAL;
  return <IconComponent color="primary" />;
};

/**
 * Check if a notification is a shift swap notification
 * @param {Object} notification - The notification object
 * @returns {boolean} - True if it's a shift swap notification
 */
const isShiftSwapNotification = (notification) => {
  return notification.type === 'SWAP_REQUESTED';
};

/**
 * Extract source name from shift swap notification message
 * @param {string|object} message - The notification message (string or object with body)
 * @returns {string} - The extracted source name or 'Someone' as fallback
 */
const extractSourceNameFromMessage = (message) => {
  let messageText = '';

  if (typeof message === 'string') {
    messageText = message;
  } else if (message && typeof message === 'object' && message.body) {
    messageText = message.body;
  }

  if (messageText) {
    // Match "from <name>" at the end of the string, allowing for whitespace
    const match = messageText.match(/\bfrom\s+([^\n\r]+?)\s*$/);
    return match && match[1] ? match[1].trim() : 'Someone';
  }

  return 'Someone';
};

/**
 * Get the main text content from a notification message
 * @param {string|object} message - The notification message (string or object with body)
 * @returns {string} - The message text or empty string
 */
const getNotificationText = (message) => {
  if (typeof message === 'string') {
    return message;
  } else if (message && typeof message === 'object' && message.body) {
    return message.body;
  }
  return '';
};

const formatNotificationDate = (timestamp) => {
  try {
    // Handle ISO string (converted from Firestore timestamp)
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp?.toDate?.() || new Date(timestamp);

    // Validate if date is valid
    if (!(date instanceof Date) || isNaN(date)) {
      return 'Just now';
    }

    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Just now';
  }
};

//  NotificationDropdown Component

const NotificationDropdown = ({ onClose }) => {
  const dispatch = useDispatch();
  const notifications = useSelector(selectNotifications);
  const status = useSelector(selectNotificationsStatus);
  const hasMore = useSelector(selectHasMoreNotifications);
  const lastDoc = useSelector(selectLastDoc);
  const currentUser = useSelector((state) => state.currentUser);
  const employeeId = currentUser?.employeeInfo?.id;
  const [filter, setFilter] = useState(FILTER_VALUES.ALL); // 'all', 'read', 'unread'
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [selectedSwapRequest, setSelectedSwapRequest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (employeeId) {
      // Initial fetch - only when dropdown opens
      dispatch(
        fetchNotificationsThunk({
          employeeId,
          filter, // Include current filter
          showMore: false, // This is an initial load
        })
      ).catch((error) => {
        console.error('Error fetching notifications:', error);
        setError('Failed to load notifications. Please try again.');
      });
    }
  }, [dispatch, employeeId, filter]); // Add filter as dependency

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (employeeId) {
        dispatch(clearNotifications());
      }
    };
  }, [dispatch, employeeId]);

  const handleLoadMore = useCallback(() => {
    if (employeeId && hasMore && lastDoc) {
      dispatch(
        fetchNotificationsThunk({
          employeeId,
          lastDoc,
          filter, // Include current filter
          showMore: true,
        })
      ).catch((error) => {
        console.error('Error loading more notifications:', error);
        setError('Failed to load more notifications. Please try again.');
      });
    }
  }, [dispatch, employeeId, hasMore, lastDoc, filter]);

  const handleMarkAsRead = useCallback((notification) => {
    if (!notification.isRead) {
      dispatch(markNotificationAsReadThunk(notification.id));
    }

    // Handle click based on notification type
    switch (notification.type) {
      case 'SHIFT_UPDATE':
        // Navigate to shift details if shiftId is available
        if (notification.data?.sid) {
          // window.location.href = `/shifts/${notification.data.sid}`;
          console.log('Navigate to shift', notification.data.sid); //TODO:Implement navigation
        }
        break;
      case 'SCHEDULE_UPDATE':
        // Navigate to schedule view
        // window.location.href = '/schedule';
        break;
      case 'SWAP_REQUESTED':
        // Don't navigate for swap requests - let the user interact with buttons
        break;
      default:
        break;
    }
  }, [dispatch]);

  const handleOpenSwapModal = useCallback((notification) => {
    // Mark notification as read
    if (!notification.isRead) {
      dispatch(markNotificationAsReadThunk(notification.id));
    }

    // Extract source name from the message using the helper function
    const sourceName = extractSourceNameFromMessage(notification.message);

    // Set up swap request data and open modal
    const swapRequest = {
      id: notification.data?.ssid,
      sourceName,
      shift: notification.data?.shift || null, // Will be fetched in modal if missing
    };

    setSelectedSwapRequest(swapRequest);
    setShowSwapModal(true);
  }, [dispatch]);

  // Close the shift swap modal
  const handleSwapModalClose = useCallback(() => {
    setShowSwapModal(false);
    setSelectedSwapRequest(null);
  }, []);

  // Handle completion of shift swap action and refresh notifications
  const handleSwapActionComplete = useCallback(() => {
    // Refresh notifications after action
    if (employeeId) {
      dispatch(
        fetchNotificationsThunk({
          employeeId,
          filter,
          showMore: false,
        })
      );
    }
  }, [dispatch, employeeId, filter]);

  /**
   * Handle filter change for notifications
   * @param {Event} event - The change event
   * @param {string} newFilter - The new filter value
   */
  const handleFilterChange = useCallback((_, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  }, []);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    if (employeeId) {
      try {
        setError(null);
        await dispatch(markAllNotificationsAsReadThunk(employeeId)).unwrap();
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
        setError('Failed to mark all notifications as read');
      }
    } else {
      console.error('No employeeId found');
      setError('User not authenticated');
    }
  }, [dispatch, employeeId]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      switch (filter) {
        case FILTER_VALUES.READ:
          return notification.isRead;
        case FILTER_VALUES.UNREAD:
          return !notification.isRead;
        default:
          return true;
      }
    });
  }, [notifications, filter]);

  // Memoize the notification rendering to prevent unnecessary re-renders
  const renderedNotifications = useMemo(() => {
    return filteredNotifications.map((notification) => (
      <React.Fragment key={notification.id}>
        {isShiftSwapNotification(notification) ? (
          // Special handling for shift swap requests - clickable to open modal
          <ShiftSwapListItem
            alignItems="flex-start"
            onClick={() => handleOpenSwapModal(notification)}
            isRead={notification.isRead}
            role="button"
            tabIndex={0}
            aria-label={`Shift swap request from ${extractSourceNameFromMessage(notification.message)}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenSwapModal(notification);
              }
            }}
          >
            <ShiftSwapListItemIcon>
              <ShiftSwapIcon />
            </ShiftSwapListItemIcon>
            <StyledListItemText
              primary={<ShiftSwapTitle variant="body1">Shift Swap Request</ShiftSwapTitle>}
              secondary={
                <Box>
                  <ShiftSwapMessage variant="body2">
                    {extractSourceNameFromMessage(notification.message)} wants to swap shifts
                  </ShiftSwapMessage>
                  <ShiftSwapTime variant="caption">{formatNotificationDate(notification.createdAt)}</ShiftSwapTime>
                </Box>
              }
              isRead={notification.isRead}
            />
            <ShiftSwapActionBox>
              <ShiftSwapActionText variant="caption">Tap to respond</ShiftSwapActionText>
              <ShiftSwapArrowIcon />
            </ShiftSwapActionBox>
          </ShiftSwapListItem>
        ) : (
          // Regular notification display
          <StyledListItem
            alignItems="flex-start"
            onClick={() => handleMarkAsRead(notification)}
            isRead={notification.isRead}
            role="button"
            tabIndex={0}
            aria-label={`Mark notification as read: ${getNotificationText(notification.message)}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleMarkAsRead(notification);
              }
            }}
          >
            <ListItemIcon>{getNotificationIcon(notification.type)}</ListItemIcon>
            <StyledListItemText
              primary={notification.message}
              secondary={formatNotificationDate(notification.createdAt)}
              isRead={notification.isRead}
            />
            {!notification.isRead && <StyledUnreadIndicator />}
          </StyledListItem>
        )}
        <Divider component="li" />
      </React.Fragment>
    ));
  }, [filteredNotifications, handleOpenSwapModal, handleMarkAsRead]);

  return (
    <NotificationContainer>
      <NotificationHeader>
        <Typography variant="h6" fontWeight={600}>Notifications</Typography>
        <MarkAllAsReadButton>
          {notifications.some((n) => !n.isRead) && (
            <Button size="small" onClick={handleMarkAllAsRead} disabled={status === 'loading'}>
              Mark all as read
            </Button>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MarkAllAsReadButton>
      </NotificationHeader>
      {error && (
        <ErrorDisplayBox>
          <Typography variant="body2">{error}</Typography>
        </ErrorDisplayBox>
      )}
      <StyledBox>
        <ToggleButtonGroup value={filter} exclusive onChange={handleFilterChange} size="small" fullWidth>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="unread">Unread</ToggleButton>
          <ToggleButton value="read">Read</ToggleButton>
        </ToggleButtonGroup>
      </StyledBox>
      <NotificationList>
        {status === 'loading' && notifications.length === 0 ? (
          <LoadingBox>
            <CircularProgress size={28} />
          </LoadingBox>
        ) : filteredNotifications.length === 0 ? (
          <EmptyNotificationsBox>
            <StyledNotificationIcon />
            <Typography>No {filter !== FILTER_VALUES.ALL ? `${filter} ` : ''}notifications</Typography>
          </EmptyNotificationsBox>
        ) : (
          renderedNotifications
        )}
      </NotificationList>
      {filteredNotifications.length > 0 && (
        <LoadMoreButton>
          <Button
            onClick={handleLoadMore}
            disabled={!hasMore || status === 'loading'}
            fullWidth
            startIcon={status === 'loading' ? <CircularProgress size={16} /> : null}
          >
            {status === 'loading' ? 'Loading...' : !hasMore ? 'End of notifications' : 'Load More'}
          </Button>
        </LoadMoreButton>
      )}
      {/* Shift Swap Response Modal */}
      <ShiftSwapModal
        open={showSwapModal}
        onClose={handleSwapModalClose}
        mode="respond"
        swapRequest={selectedSwapRequest}
        onActionComplete={handleSwapActionComplete}
      />
    </NotificationContainer>
  );
};

export default NotificationDropdown;

NotificationDropdown.propTypes = {
  onClose: PropTypes.func.isRequired,
};
